import { useEffect, useRef } from "react";
import type { ReaderSettings, ReaderTheme } from "@/hooks/useReaderSettings";

interface ReaderSettingsPanelProps {
  settings: ReaderSettings;
  onFontSize: (size: number) => void;
  onTheme: (theme: ReaderTheme) => void;
  onClose: () => void;
}

const themes: { key: ReaderTheme; label: string; swatch: string }[] = [
  { key: "light", label: "浅色", swatch: "bg-background-50 border-background-300" },
  { key: "eye", label: "护眼", swatch: "bg-accent-100 border-accent-300" },
  { key: "dark", label: "黑夜", swatch: "bg-foreground-900 border-foreground-700" },
];

export default function ReaderSettingsPanel({
  settings,
  onFontSize,
  onTheme,
  onClose,
}: ReaderSettingsPanelProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full z-30 mt-2 w-full max-w-xs rounded-lg border border-background-300 bg-background-50 p-4 sm:w-72"
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-heading text-sm font-semibold text-foreground-900">阅读设置</h3>
        <button
          type="button"
          onClick={onClose}
          className="flex h-6 w-6 items-center justify-center rounded text-foreground-400 hover:bg-background-100 hover:text-foreground-700"
          aria-label="关闭设置"
        >
          <i className="ri-close-line"></i>
        </button>
      </div>

      <div className="mb-4">
        <p className="mb-2 text-xs text-foreground-500">字体大小</p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onFontSize(Math.max(14, settings.fontSize - 1))}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            aria-label="缩小字体"
          >
            <i className="ri-subtract-line"></i>
          </button>
          <span className="flex-1 text-center text-sm font-medium text-foreground-900">
            {settings.fontSize} px
          </span>
          <button
            type="button"
            onClick={() => onFontSize(Math.min(28, settings.fontSize + 1))}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            aria-label="放大字体"
          >
            <i className="ri-add-line"></i>
          </button>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs text-foreground-500">阅读背景</p>
        <div className="grid grid-cols-3 gap-2">
          {themes.map((theme) => (
            <button
              key={theme.key}
              type="button"
              onClick={() => onTheme(theme.key)}
              className={`flex flex-col items-center gap-1.5 rounded-md border p-2 transition-colors ${
                settings.theme === theme.key
                  ? "border-primary-400"
                  : "border-background-200 hover:border-background-300"
              }`}
            >
              <span className={`h-7 w-full rounded border ${theme.swatch}`}></span>
              <span
                className={`text-xs ${
                  settings.theme === theme.key
                    ? "font-medium text-primary-600"
                    : "text-foreground-600"
                }`}
              >
                {theme.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}